import { cn } from '@/utilities/ui'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded text-sm font-medium ring-offset-background transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    defaultVariants: {
      size: 'default',
      variant: 'default',
    },
    variants: {
      size: {
        clear: '',
        default: 'h-10 px-4 py-2',
        icon: 'h-10 w-10',
        lg: 'h-11 rounded px-8',
        sm: 'h-9 rounded px-3',
      },
      variant: {
        default: 'inline-flex items-center h-10 bg-minigreen-2 hover:bg-minigreen-1 rounded-full text-2xl p-6 border-4 border-minigreen-2 hover:border-minigreen-1 transition-color duration-300 ease-in-out font-sans font-semibold text-mini-primary',
        // default: 'inline-flex items-center rounded-full bg-minigreen-6 text-white dark:bg-minigreen-1 dark:text-black px-6 py-3 text-base font-semibold shadow-sm hover:bg-minigreen-7 transition',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        ghost: 'hover:bg-card hover:text-accent-foreground',
        link: 'text-lg items-start justify-start underline-offset-4 hover:underline',
        outline: 'border-4 border-minigreen-5 hover:border-minigreen-6 rounded-full text-2xl p-6 text-white hover:bg-minigreen-6 transition-color duration-300 ease-in-out font-sans',
        primary: 'inline-flex items-center rounded-full bg-minigreen-6 text-white dark:bg-minigreen-2 dark:hover:bg-minigreen-1 dark:text-black px-6 py-3 text-base font-semibold hover:bg-minigreen-7 transition',
        secondary: 'inline-flex items-center justify-center rounded-full border-2 border-minigreen-7 text-minigreen-7 dark:border-minigreen-1 dark:text-minigreen-1 px-6 py-3 text-base font-semibold font-semibold hover:bg-minigreen-1 dark:hover:bg-minigreen-bg-dark transition',
        'hero-primary': 'bg-minigreen-2 hover:bg-minigreen-2 text-mini-primary rounded-full text-2xl p-6 border-4 border-minigreen-2 hover:border-minigreen-1 transition-color duration-300 ease-in-out',
        'hero-secondary': 'border-4 border-minigreen-5 hover:border-minigreen-6 rounded-full text-2xl p-6 text-white hover:bg-minigreen-6 transition-color duration-300 ease-in-out',
      },
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

const Button: React.FC<ButtonProps> = ({
  asChild = false,
  className,
  size,
  variant,
  ref,
  ...props
}) => {
  const Comp = asChild ? Slot : 'button'
  return <Comp className={cn(buttonVariants({ className, size, variant }))} ref={ref} {...props} />
}

export { Button, buttonVariants }
